import { createSlsTracker, ErrorCapture, PerformanceMonitor } from 'al-sls-web-track';
import myJsTools from "@/utils/myJsTools";
import { getPlatform } from '@tencentcloud/universal-api';
import store from "@/store/store";

// 创建追踪器实例
const userInfo = myJsTools.getItem('docUserInfo') || {};
const originalTracker = process.env.VUE_APP_ENV === 'development' ? new Proxy({}, {
  get(target, prop) {
    return function (...args) {
      // console.debug(`Method "${prop}" is called, but does not exist or ignored.`);
      return undefined; // 或 Promise.resolve()
    };
  }
}) : createSlsTracker({
  env: process.env.VUE_APP_LOG_ENV,
  logstore: 'cloud-hos-h5',
  userId: userInfo.userID,
  trackerType: 'browser',
  time: 10,
  count: 10,
  topic: getPlatform() === 'pc' ? 'web-doctor-log' : 'h5-doctor-log',
  source: getPlatform() === 'pc' ? 'web-doctor' : 'h5-doctor',
  tags: {
    version:  process.env.VUE_APP_BUILD_TIME_VERSION, // 当前运行的版本时间戳
    platform: getPlatform() === 'pc' ? 'web' : 'h5'
  }
});

// 启用自动错误捕获
const errorCapture = new ErrorCapture(originalTracker);

// 创建性能监控实例
const performanceMonitor = new PerformanceMonitor(originalTracker);

/**
 * 添加公共参数可以在当前方法返回的对象中追加
 * @returns {Object}
 */
function getCommonContext(logData) {
    const docName = myJsTools.getItem('docUserInfo')?.docName || '';
    return {
      docName: logData?.docName || docName || '',
    };
}

/**
 * 包装后的 tracker，自动添加公共参数
 */
export const tracker = new Proxy(originalTracker, {
  get(target, prop) {
    // 拦截 send 方法
    if (prop === 'send') {
      return function(logData, isSync = false) {
          const commonContext = getCommonContext(logData);
          
          // 合并公共参数
          const enhancedLogData = {
            ...logData,
            ...commonContext
          };
          return target?.send(enhancedLogData, isSync);
      };
    }
    
    // 拦截 sendError 方法
    if (prop === 'sendError') {
      return function(errorData, isSync = false) {
          const commonContext = getCommonContext(errorData);
          
          const enhancedErrorData = {
            ...errorData,
            ...commonContext
          };
          
          return target?.sendError(enhancedErrorData, isSync);
      };
    }
    
    // 其他方法直接返回原始方法（自动绑定 this）
    const value = target[prop];
    if (typeof value === 'function') {
      return value.bind(target);
    }
    return value;
  }
});

/**
 * 获取网络信息
 */
function getNetworkInfo() {
  const nav = navigator;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!conn) {
    return {
      supported: false,
      message: 'Network Information API not supported',
      isOnline: navigator.onLine,
      timestamp: new Date().toISOString()
    };
  }

  return {
    supported: true,
    downlink: conn.downlink || null,           // 下行速度 Mbps
    downlinkMax: conn.downlinkMax || null,     // 最大下行速度
    effectiveType: conn.effectiveType || null, // '4g', '3g', '2g', 'slow-2g'
    rtt: conn.rtt || null,                     // 往返时间 ms
    saveData: !!conn.saveData,                 // 是否开启省流量模式
    type: conn.type || null,                   // 'wifi', 'cellular', 'bluetooth', 'ethernet', 'none'
    isOnline: navigator.onLine,                // 是否在线
    timestamp: new Date().toISOString()
  };
}

/**
 * 获取网络类型的可读文本
 */
function getNetworkTypeText(info) {
  if (!info.isOnline) return '离线';
  
  const typeMap = {
    'wifi': 'WiFi',
    'cellular': '移动网络',
    'bluetooth': '蓝牙',
    'ethernet': '以太网',
    'none': '无网络',
    'unknown': '未知'
  };
  
  const effectiveTypeMap = {
    '4g': '4G',
    '3g': '3G',
    '2g': '2G',
    'slow-2g': '慢速2G'
  };
  
  const type = typeMap[info.type] || '未知';
  const effectiveType = effectiveTypeMap[info.effectiveType] || '';
  
  // 如果是移动网络,显示具体的网络类型
  if (info.type === 'cellular' && effectiveType) {
    return `${effectiveType}(${type})`;
  }
  
  return type;
}

/**
 * 对比网络信息变化
 */
function getNetworkChanges(oldInfo, newInfo) {
  const changes = [];
  
  // 在线状态变化
  if (oldInfo.isOnline !== newInfo.isOnline) {
    changes.push({
      field: 'isOnline',
      oldValue: oldInfo.isOnline ? '在线' : '离线',
      newValue: newInfo.isOnline ? '在线' : '离线',
      description: newInfo.isOnline ? '网络已恢复' : '网络已断开'
    });
  }
  
  // 网络类型变化 (wifi/cellular)
  if (oldInfo.type !== newInfo.type) {
    const oldType = getNetworkTypeText(oldInfo);
    const newType = getNetworkTypeText(newInfo);
    changes.push({
      field: 'type',
      oldValue: oldType,
      newValue: newType,
      description: `网络类型从 ${oldType} 变为 ${newType}`
    });
  }
  
  // 有效类型变化 (4g/3g/2g)
  if (oldInfo.effectiveType !== newInfo.effectiveType) {
    changes.push({
      field: 'effectiveType',
      oldValue: oldInfo.effectiveType || '未知',
      newValue: newInfo.effectiveType || '未知',
      description: `网络速度从 ${oldInfo.effectiveType || '未知'} 变为 ${newInfo.effectiveType || '未知'}`
    });
  }
  
  return changes;
}

/**
 * 生成网络变化摘要
 */
function generateNetworkSummary(changes, oldInfo, newInfo) {
  if (changes.length === 0) {
    return '网络状态无明显变化';
  }
  
  const descriptions = changes.map(change => change.description);
  
  return descriptions.join('; ');
}

// 保存上一次的网络信息
let previousNetworkInfo = getNetworkInfo();

/**
 * 监听 Network Information API 变化
 */
if ('connection' in navigator) {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  connection.addEventListener('change', () => {
    const currentInfo = getNetworkInfo();
    const changes = getNetworkChanges(previousNetworkInfo, currentInfo);
    const summary = generateNetworkSummary(changes, previousNetworkInfo, currentInfo);
    // console.log('test---network000', {
    //   message: `#NETWORK#网络变化-${summary}`,
    //   content: {
    //     summary,
    //     changes,
    //     current: currentInfo,
    //     previous: previousNetworkInfo,
    //     networkType: getNetworkTypeText(currentInfo)
    //   }
    // })
    tracker.send({
      message: `#NETWORK#网络变化-${summary}`,
      content: {
        summary,
        changes,
        current: currentInfo,
        previous: previousNetworkInfo,
        networkType: getNetworkTypeText(currentInfo)
      }
    });
    
    // 更新上一次的信息
    previousNetworkInfo = currentInfo;
  });
}

/**
 * 监听在线状态变化
 */
window.addEventListener('online', async () => {
  const currentInfo = getNetworkInfo();
  const changes = getNetworkChanges(previousNetworkInfo, currentInfo);
  const summary = `网络已恢复 - ${getNetworkTypeText(currentInfo)}`;
  
  // console.log('test---network111', {
  //   message: `#NETWORK#网络恢复-${summary}`,
  //   level: 'info',
  //   content: {
  //     summary,
  //     changes,
  //     current: currentInfo,
  //     previous: previousNetworkInfo,
  //     networkType: getNetworkTypeText(currentInfo)
  //   }
  // })
  
  tracker.send({
    message: `#NETWORK#网络恢复-${summary}`,
    level: 'info',
    content: {
      summary,
      changes,
      current: currentInfo,
      previous: previousNetworkInfo,
      networkType: getNetworkTypeText(currentInfo)
    }
  });
  
  previousNetworkInfo = currentInfo;

  let docId = '';
  if (myJsTools.getItem('docUserInfo') && myJsTools.getItem('docUserInfo').docId) {
    docId = myJsTools.getItem('docUserInfo').docId;
  };
  await store.dispatch('msg/closeWebSocket');
  docId && store.dispatch('msg/initWebSocket', docId);
});

window.addEventListener('offline', () => {
  store.dispatch('msg/closeWebSocket');
  const currentInfo = getNetworkInfo();
  const changes = getNetworkChanges(previousNetworkInfo, currentInfo);
  const summary = '网络已断开';

  // console.log('test---network222', {
  //   message: `#NETWORK#网络断开-${summary}`,
  //   level: 'warning',
  //   content: {
  //     summary,
  //     changes,
  //     current: currentInfo,
  //     previous: previousNetworkInfo,
  //     networkType: '离线'
  //   }
  // })
  
  tracker.send({
    message: `#NETWORK#网络断开-${summary}`,
    level: 'warning',
    content: {
      summary,
      changes,
      current: currentInfo,
      previous: previousNetworkInfo,
      networkType: '离线'
    }
  });
  
  previousNetworkInfo = currentInfo;
});

// Promise 错误处理
window.addEventListener('unhandledrejection', (event) => {
  const getReadableReason = (reason) => {
    if (!reason) return 'Unknown reason';

    if (reason instanceof Error) {
      return reason.message;
    }

    if (typeof reason === 'object') {
      // 优先提取常见错误消息字段
      const priorityKeys = ['msg', 'message', 'errorMsg', 'description', 'error'];
      for (const key of priorityKeys) {
        if (reason[key] !== undefined) {
          return String(reason[key]);
        }
      }

      // 兜底：完整序列化
      try {
        return JSON.stringify(reason, null, 2);
      } catch {
        return '[Circular or non-serializable object]';
      }
    }

    return String(reason);
  };

  const errorData = {
    errorMessage: getReadableReason(event.reason),
    errorStack: event.reason?.stack || 'No stack trace',
    errorType: 'promise',
    reasonType: typeof event.reason,
    reasonConstructor: event.reason?.constructor?.name || 'Unknown',
    timestamp: new Date().toLocaleTimeString(),
  };

  tracker.sendError(errorData);
});

// 导出方法供外部使用
export const getNetworkStatus = () => {
  const info = getNetworkInfo();
  return {
    info,
    networkType: getNetworkTypeText(info)
  };
};